import React, { useMemo } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { useGPS } from '../../contexts/GPSContext';

const COLORS = [
  '#5b3df0', '#2f6df0', '#f5a623', '#16a866', '#36a7d6', '#7c5cff', '#e5484d', '#2bbfae', '#9b8cf5', '#f0a35e'
];

const SUBTITLES = {
  all: 'Encours à fin de période · sur les tickets ouverts',
  ongoing: 'Encours à fin de période · sur les tickets ouverts',
  received: 'Tickets reçus · sur la période, par type',
  resolved: 'Tickets fermés · sur la période, par type',
};

const GPSRequestTypeChart = ({ className = '', sense = 'all' }) => {
  const gps = useGPS();

  const data = useMemo(() => {
    const issues = Array.isArray(gps?.summarySensedIssues) ? gps.summarySensedIssues : [];
    if (issues.length === 0) return [];

    const map = new Map();
    issues.forEach(issue => {
      const key = issue?.request_type || issue?.requestType || 'Non spécifié';
      map.set(key, (map.get(key) || 0) + 1);
    });

    return Array.from(map.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [gps?.summarySensedIssues]);

  if (!data.length) {
    return (
      <div className="bg-white dark:bg-[#1e1e1e] p-6 rounded-2xl border border-gray-200 dark:border-gray-900 h-full flex items-center justify-center">
        <p className="text-gray-600 dark:text-gray-400 text-center">Aucune donnée par type de requête.</p>
      </div>
    );
  }

  const total = data.reduce((sum, item) => sum + item.value, 0);
  const top = data[0];
  const topPct = total > 0 ? Math.round((top.value / total) * 100) : 0;

  return (
    <div className={`bg-white dark:bg-[#1e1e1e] p-5 sm:p-6 rounded-2xl border border-gray-200 dark:border-gray-900 h-full flex flex-col ${className}`}>
      <div className="flex items-center gap-3 mb-5">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#f3f1fe] dark:bg-[#2a2440] text-[#5b3df0] dark:text-[#a98ff5] flex-none">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
            <path d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
          </svg>
        </span>
        <div className="min-w-0">
          <span className="block text-base font-bold text-gray-900 dark:text-white">Répartition par type de requête</span>
          <span className="block text-xs text-gray-500 dark:text-gray-400">{SUBTITLES[sense] || SUBTITLES.all}</span>
        </div>
      </div>

      <div className="flex-1 flex items-center gap-5 sm:gap-6 min-h-[200px]">
        <div className="relative flex-none" style={{ width: 168, height: 168 }}>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-2xl font-extrabold leading-none text-gray-900 dark:text-white">{topPct}%</span>
            <span className="mt-1 text-[11px] font-semibold text-gray-400 dark:text-gray-500 max-w-[92px] truncate text-center">{top.name}</span>
          </div>
          <ResponsiveContainer width={168} height={168}>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={82}
                innerRadius={58}
                paddingAngle={1.2}
                startAngle={90}
                endAngle={-270}
                label={false}
                stroke="none"
              >
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                wrapperStyle={{ zIndex: 30 }}
                formatter={(value, name) => {
                  const percent = ((value / total) * 100).toFixed(1);
                  return [`${value} (${percent}%)`, name];
                }}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-2.5 text-xs overflow-auto max-h-[180px] pr-1">
          {data.map((item, index) => {
            const pct = ((item.value / total) * 100).toFixed(1).replace('.', ',');
            return (
              <span key={`lg-${index}`} className="flex items-center gap-2.5 font-semibold text-gray-600 dark:text-gray-300">
                <span className="w-2.5 h-2.5 rounded-[3px] flex-none" style={{ background: COLORS[index % COLORS.length] }} />
                <span className="truncate" title={item.name}>{item.name}</span>
                <span className="ml-auto pl-2 text-gray-900 dark:text-white whitespace-nowrap">{pct} %</span>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GPSRequestTypeChart;
